import { sendEvent, sendGAEvent } from "~/app/analytics/ga/ga";

type TrackableProductField = "productId" | "quantity" | "requestDeliveryDate" | "priceReference" | "targetPrice";
export interface BulkUploadAnalyticsPreviewItem {
  productId: string;
  quantity: number;
  requestDeliveryDate?: string;
  priceReference: string;
  targetPrice?: number | null;
}
type TrackProductInteractionParams = {
  successful: boolean;
  field: TrackableProductField;
  previousItem: BulkUploadAnalyticsPreviewItem;
  updatedItem: BulkUploadAnalyticsPreviewItem;
};
export interface BulkUploadAnalyticsProduct {
  id: string;
  quantity: number;
  date?: string;
  price_reference?: string;
  target_price?: number;
}
const TRACKED_PRODUCT_FIELD_LABELS: Record<TrackableProductField, string> = {
  productId: "Product ID",
  quantity: "Quantity",
  requestDeliveryDate: "Date",
  priceReference: "Price Reference",
  targetPrice: "Target Price",
};
const CATEGORY = "Bulk Upload Interactions";

export const sendPopupOpen = () =>
  sendGAEvent({
    eventCategory: CATEGORY,
    eventAction: "Open popup",
  });
export const addToCart = (amount: number) =>
  sendEvent({
    event: "addToCart",
    eventCategory: CATEGORY,
    eventAction: "Add to cart",
    eventLabel: `${amount} product(s)`,
  });
export const overrideCart = (amount: number) =>
  sendEvent({
    event: "OverrideCart",
    eventCategory: CATEGORY,
    eventAction: "Override Cart",
    eventLabel: `${amount} product(s)`,
  });

export const trackClose = (closeButtonType: "Cancel Button" | "X Button") =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Pop Up Closed",
    raw_action: "Click",
    extra_information: `${closeButtonType}`,
  });

export const trackFileUpload = (successful: boolean, fileType?: string) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "File Uploaded",
    raw_action: "Click",
    type: fileType || "unknown",
    status: successful ? "Successful" : "Unsuccessful",
  });

export const trackTextUpload = (successful: boolean) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Text Uploaded",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
  });

export const trackProductInteraction = ({
  successful,
  field,
  previousItem,
  updatedItem,
}: TrackProductInteractionParams) => {
  const gaData = {
    event: "Bulk_Upload_Related_Interaction",
    action: "Product Interacted",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
  };

  return sendEvent({
    ...gaData,
    field: TRACKED_PRODUCT_FIELD_LABELS[field],
    id: updatedItem.productId,
    quantity: updatedItem.quantity,
    previous_value: previousItem[field],
    date: updatedItem.requestDeliveryDate,
    price_reference: updatedItem.priceReference,
    target_price: updatedItem.targetPrice,
  });
};

export const trackProductRemoved = ({
  successful,
  removedItem,
}: {
  successful: boolean;
  removedItem: BulkUploadAnalyticsPreviewItem;
}) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Product Removed",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
    field: "Product ID",
    id: removedItem.productId,
    quantity: removedItem.quantity,
    previous_value: removedItem.productId,
    date: removedItem.requestDeliveryDate,
    price_reference: removedItem.priceReference,
    target_price: removedItem.targetPrice,
  });

export const trackPriceReferenceSelectionForAllInitiated = (products: BulkUploadAnalyticsProduct[]) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Product Price Reference Selection For All Initiated",
    raw_action: "Click",
    products,
  });

export const trackPriceReferenceSelectionForAllSave = (
  products: BulkUploadAnalyticsProduct[],
  successful: boolean,
  priceReferenceChosen: string,
) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Product Price Reference Selected For All",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
    price_reference_chosen: `${priceReferenceChosen}`,
    products,
  });

export const trackSwitchProductVersionForAllInitiated = (products: BulkUploadAnalyticsProduct[]) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Use Newest Product Versions Initiated",
    raw_action: "Click",
    products,
  });

export const trackSwitchProductVersionForAllSave = (products: BulkUploadAnalyticsProduct[], successful: boolean) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Newest Product Versions Applied For All",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
    products,
  });

export const trackCommentPosted = (successful: boolean) =>
  sendEvent({
    event: "Bulk_Upload_Related_Interaction",
    action: "Comment Posted",
    raw_action: "Click",
    status: successful ? "Successful" : "Unsuccessful",
  });
